import { z } from 'zod';
/**
 * Wire schemas for the measurement-report REST API.
 *
 * The AP entry mirrors `struct rtw_scan_result` (wifi/component/wifi/api/wifi_api_types.h:796)
 * returned by `wifi_get_scan_records()` (wifi_api.h:174). Only `bssid` and `rssi` are
 * mandatory — everything else is optional so a minimal MCU implementation can keep
 * its JSON payload small, while a full implementation can forward every field.
 */
export const macAddressSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?:[0-9a-f]{2}:){5}[0-9a-f]{2}$/, 'bssid must be aa:bb:cc:dd:ee:ff');
/** "scan" = plain wifi_scan_networks() result; "beacon_report_11k" = 802.11k RRM beacon report. */
export const measurementSourceSchema = z.enum(['scan', 'beacon_report_11k']);
export const apMeasurementSchema = z
    .object({
    /** `rtw_scan_result.bssid` */
    bssid: macAddressSchema,
    /** `rtw_scan_result.ssid` */
    ssid: z.string().max(32).optional(),
    /**
     * `rtw_scan_result.signal_strength` (s16, dBm).
     * Accepts the alias `rssi` for convenience (see preprocessing below).
     */
    signal_strength: z.number().int().min(-120).max(0).optional(),
    /** `rtw_scan_result.channel` */
    channel: z.number().int().min(1).max(233).optional(),
    /** `rtw_scan_result.band` — enum rtw_supported_band (0=2.4G, 1=5G, 2=6G) */
    band: z.number().int().min(0).max(2).optional(),
    /** `rtw_scan_result.security` — raw u32 enum rtw_security_flag bitmask */
    security: z.number().int().nonnegative().optional(),
    /** `rtw_scan_result.wireless_mode` — RTW_80211_* bitmask */
    wireless_mode: z.number().int().nonnegative().optional(),
    /** `rtw_scan_result.bss_type` */
    bss_type: z.number().int().nonnegative().optional(),
    /** `rtw_scan_result.wps_type` */
    wps_type: z.number().int().nonnegative().optional(),
    /** `rtw_scan_result.country_code[2]` as a 2-char ISO-3166 string */
    country_code: z.string().length(2).optional(),
    /** `rtw_scan_result.is_beacon` */
    is_beacon: z.number().int().min(0).max(1).optional(),
    /** 802.11k only: RCPI in 0.5 dBm steps (IEEE 802.11-2020 §9.4.2.38) */
    rcpi: z.number().int().min(0).max(255).optional(),
    /** 802.11k only: RSNI in 0.5 dB steps */
    rsni: z.number().int().min(0).max(255).optional(),
    /** 802.11k only: measurement token from the Radio Measurement Request */
    meas_token: z.number().int().optional(),
})
    .passthrough();
/** Position on a floor plan, metres, origin at the image's bottom-left. */
export const positionSchema = z.object({
    floor_id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    z: z.number().optional(),
});
/**
 * Ground truth by reference-point label: the device only knows "I am at point P3 on floor 1".
 * The backend resolves it to the reference point placed on the map beforehand.
 *
 * `floor_id` accepts the floor's generated id **or** its name — firmware cannot discover a uuid,
 * so it sends the name shown in the UI, and the backend (which keeps floor names unique)
 * canonicalises it to the id on ingest. The same applies to `positionSchema.floor_id` on a report.
 */
export const groundTruthLabelSchema = z.object({
    floor_id: z.string().min(1),
    label: z.string().trim().min(1).max(64),
});
export const groundTruthSchema = z.union([positionSchema, groundTruthLabelSchema]);
export const isLabelGroundTruth = (gt) => 'label' in gt;
export const reportSchema = z.object({
    device_id: z.string().min(1).max(64),
    /** ISO-8601 timestamp. Omitted by devices without RTC — the server stamps arrival time. */
    ts: z.string().datetime({ offset: true }).optional(),
    /** Device uptime in ms (`rtos_time_get_current_system_time_ms()`), used when `ts` is absent. */
    uptime_ms: z.number().int().nonnegative().optional(),
    seq: z.number().int().nonnegative().optional(),
    source: measurementSourceSchema.default('scan'),
    /** Return value of `wifi_scan_networks()`; may exceed `aps.length` if the device truncates. */
    scan_ap_num: z.number().int().nonnegative().optional(),
    /** BSSID the device is currently associated with, if any. */
    connected_bssid: macAddressSchema.optional(),
    aps: z.array(apMeasurementSchema).min(1).max(128),
    /** Known location at capture time — set when walking a calibration grid. */
    ground_truth: groundTruthSchema.optional(),
    /** Free-form device/run labels, e.g. { fw: "1.2.3", 11k: "on" } */
    meta: z.record(z.string()).optional(),
});
/** POST /api/v1/reports accepts one report or a batch. */
export const reportIngestSchema = z.union([reportSchema, z.array(reportSchema).min(1).max(200)]);
export const floorSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(128),
    /** Image served from /uploads/<image_path>; optional until a plan is uploaded. */
    image_path: z.string().nullable().optional(),
    /** Floor-plan extent in metres (defines the Leaflet CRS.Simple bounds). */
    width_m: z.number().positive(),
    height_m: z.number().positive(),
    /** Optional geo-anchor so local XY can later be projected onto a real map. */
    origin_lat: z.number().min(-90).max(90).nullable().optional(),
    origin_lon: z.number().min(-180).max(180).nullable().optional(),
    rotation_deg: z.number().nullable().optional(),
});
export const floorCreateSchema = floorSchema.partial({ id: true });
export const referencePointSchema = z.object({
    id: z.string().min(1),
    floor_id: z.string().min(1),
    label: z.string().min(1).max(64),
    x: z.number(),
    y: z.number(),
    z: z.number().default(0),
});
export const referencePointCreateSchema = referencePointSchema.partial({ id: true, z: true });
/** POST /api/v1/reports/:id/fingerprint */
export const fingerprintCreateSchema = z
    .object({
    reference_point_id: z.string().min(1).optional(),
    /** Alternative to reference_point_id: create/attach a point at these coordinates. */
    position: positionSchema.optional(),
    label: z.string().max(64).optional(),
})
    .refine((v) => v.reference_point_id !== undefined || v.position !== undefined, {
    message: 'either reference_point_id or position is required',
});
export const estimateAlgorithmSchema = z.enum(['knn', 'wknn', 'multilateration']);
export const estimateRequestSchema = z
    .object({
    report_id: z.string().optional(),
    report: reportSchema.optional(),
    algorithm: estimateAlgorithmSchema.default('wknn'),
    k: z.number().int().min(1).max(50).default(4),
    floor_id: z.string().optional(),
    /** Restrict fingerprints to a measurement source, for 11k-on vs 11k-off comparison. */
    source: measurementSourceSchema.optional(),
    /** Exclude a fingerprint id — used internally by leave-one-out evaluation. */
    exclude_fingerprint_id: z.string().optional(),
})
    .refine((v) => v.report_id !== undefined || v.report !== undefined, {
    message: 'either report_id or report is required',
});
export const evaluateRequestSchema = z.object({
    floor_id: z.string().optional(),
    algorithm: estimateAlgorithmSchema.default('wknn'),
    /** Sweep several k values in one run; defaults to [1,3,4,5,7]. */
    k_values: z.array(z.number().int().min(1).max(50)).min(1).max(10).optional(),
    source: measurementSourceSchema.optional(),
    /** Number of cross-validation folds (grouped by reference point). */
    folds: z.number().int().min(2).max(20).optional(),
});
export const apPositionUpdateSchema = z.object({
    floor_id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    z: z.number().optional(),
});
//# sourceMappingURL=api.js.map